/**
 * IGRA-21 — Subscription screen state (`GET /vpn/overview` + `POST /vpn/activate-trial`).
 * Follows the existing `useAffiliateStore`/`useProfileStore` pattern in this
 * package: plain Pinia options store, loading/error flags, no optimism on
 * financial state (activation only reflects in `overview` after the backend
 * call resolves).
 */
import { defineStore } from 'pinia'
import { VPN_UPSTREAM_UNAVAILABLE } from '@/shared/api/mocks/vpnFixtures'
import { activateTrial, fetchOverview, issueKey } from '../api/vpnApi'
import { reportTimezone } from '../api/timezoneApi'
import { VPN_CAPTCHA_INVALID } from '../api/publicCheckoutApi'
import type { VpnCardBinding, VpnOverviewResponse } from '../types/vpn'
import { rememberReferralOffer } from '../composables/useFirstReferralOffer'

interface VpnState {
  overview: VpnOverviewResponse | null
  loading: boolean
  overviewRequestId: number
  error: string | null
  activating: boolean
  activateError: string | null
  /**
   * HK-IGRA.127 — счёт привязки карты, который бэкенд отдал вместо ключа:
   * пробный доступ выдаётся только после привязки. `null` — привязка не
   * нужна (карта уже есть) либо счёт уже отработан.
   */
  cardBinding: VpnCardBinding | null
  /** HK-IGRA.664 — идёт ли сейчас выпуск ключа («Подключить устройство»). */
  issuingKey: boolean
  /** i18n-ключ причины, по которой ключ выпустить не удалось; `null` — всё хорошо. */
  issueKeyError: string | null
}

/** Maps a thrown activate-trial error to an i18n key for a human-readable
 * message (task acceptance: "VPN_UPSTREAM_UNAVAILABLE при активации →
 * человекочитаемая ошибка"). Exported for unit testing. */
export function classifyActivateTrialError(error: unknown): string {
  const code = (error as { response?: { data?: { error_message?: string; error_code?: string } } })
    ?.response?.data?.error_message
    ?? (error as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code
  if (code === VPN_UPSTREAM_UNAVAILABLE) {
    return 'vpn.dashboard.errors.activateUpstreamUnavailable'
  }
  // IGRA-33: серверная капча (CaptchaService → VpnCaptchaInvalidException).
  if (code === VPN_CAPTCHA_INVALID) {
    return 'vpn.dashboard.errors.captchaInvalid'
  }
  return 'vpn.dashboard.errors.activateGeneric'
}

export const useVpnStore = defineStore('vpn', {
  state: (): VpnState => ({
    overview: null,
    loading: false,
    overviewRequestId: 0,
    error: null,
    activating: false,
    activateError: null,
    cardBinding: null,
    issuingKey: false,
    issueKeyError: null,
  }),

  actions: {
    async fetchOverview(forceFresh = false) {
      if (this.loading && !forceFresh) return

      const requestId = ++this.overviewRequestId
      this.loading = true
      this.error = null

      // IGRA-411 — заодно сообщаем часовой пояс: он нужен «Ночной смене», и
      // кабинет единственное место, где браузер вообще о себе рассказывает.
      // Без await: пояс не то, ради чего человек должен ждать обзор.
      void reportTimezone()

      try {
        const overview = await fetchOverview()
        if (requestId === this.overviewRequestId) {
          if (overview.first_referral_offer) rememberReferralOffer(overview.first_referral_offer)
          this.overview = overview
        }
      } catch (error) {
        if (requestId !== this.overviewRequestId) return
        console.warn('[igra.now][vpn] Failed to load overview', error)
        this.error = 'vpn.dashboard.errors.loadFailed'
      } finally {
        if (requestId === this.overviewRequestId) this.loading = false
      }
    },

    /** Guard against double-submit (task acceptance: "двойной клик по
     * «Активировать» → один запрос"): a second call while the first is
     * in-flight is a no-op.
     *
     * IGRA-33: `captchaToken` приходит из CaptchaWidget (тот же механизм, что
     * на /buy и в RegistrationModal). Без токена запрос не уходит в сеть —
     * бэкенд всё равно отказал бы капчей, показываем понятное сообщение. */
    async activateTrial(captchaToken: string) {
      if (this.activating) return

      if (!captchaToken) {
        this.activateError = 'vpn.dashboard.errors.captchaInvalid'
        return
      }

      this.activating = true
      this.activateError = null
      this.cardBinding = null

      try {
        const result = await activateTrial(captchaToken)

        // HK-IGRA.127 — карты нет: доступ не выдан, вместо него пришёл счёт
        // привязки на 1 ₽ (он вернётся). Ключ/доступ в этом ответе `null`, и
        // мерджить их в overview НЕЛЬЗЯ — это стёрло бы то, что уже показано
        // на экране. Экран открывает оплату по `cardBinding`; сам пробный
        // доступ выдаст вебхук, второго запроса отсюда не требуется.
        if (result.card_binding) {
          this.cardBinding = result.card_binding
          return
        }

        if (this.overview) {
          // Merge in place — activate-trial only returns `key`/`access`
          // (API-CONTRACT.md), `pricing`/`circle` are untouched.
          this.overview = { ...this.overview, key: result.key, access: result.access }
        } else {
          // Should not happen in practice: the CTA only renders once an
          // overview has already loaded. Refetch as a safe fallback.
          await this.fetchOverview()
        }
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to activate trial', error)
        this.activateError = classifyActivateTrialError(error)
      } finally {
        this.activating = false
      }
    },

    /**
     * HK-IGRA.664 — выпустить ключ оплатившему, у которого его нет.
     *
     * Состояние «доступ живой, ключа нет» оставили оплаты из кабинета,
     * сделанные до HK-IGRA.664. Кнопка «Подключить устройство» на «Главной» —
     * единственный путь починки, доступный самому человеку.
     *
     * Перечитываем ОБЗОР ЦЕЛИКОМ, а не вмерживаем ключ из ответа: вместе с
     * ключом у человека меняется весь кабинет (появляется раздел «Ключ»,
     * блок подключения начинает показывать ссылку), и собирать эту правду
     * двумя источниками значило бы однажды их разъехать.
     *
     * Возвращает `true` — экран может уводить на страницу подключения.
     */
    async issueKey(): Promise<boolean> {
      if (this.issuingKey) return false

      this.issuingKey = true
      this.issueKeyError = null

      try {
        await issueKey()
        await this.fetchOverview(true)

        return true
      } catch (error) {
        console.warn('[igra.now][vpn] Failed to issue key', error)
        this.issueKeyError = 'vpn.dashboard.connectSetup.issueError'

        return false
      } finally {
        this.issuingKey = false
      }
    },
  },
})
